import {
  PathsV1VehicleBuyInSearchPostParametersQueryExtend,
  PathsV1VehicleBuyInSearchPostParametersQueryOrderByDirection,
  PathsV1VehicleBuyInSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { UNLIMITED_PAGE_SIZE } from "@/common/api/_defaults";
import { useVehicleBuyInSearchQuery } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-search.query";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { UserItem, UserItemVariant } from "@/components/user-item/user-item";
import { VehicleBuyInStatusBadge } from "@/components/vehicle-buy-in/vehicle-buy-in-status-badge";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

const SKELETON_ROW_COUNT = 2;
const VEHICLE_BUY_IN_REFETCH_INTERVAL_MS = 10_000;

interface VehicleValuationBuyInsCardProps {
  vehicleValuationUuid: string;
}

export function VehicleValuationBuyInsCard({ vehicleValuationUuid }: VehicleValuationBuyInsCardProps) {
  const { t } = useTranslation(["fab", "common"]);
  const searchOptions = useMemo(
    () => ({
      parameters: {
        page: 1,
        pageSize: UNLIMITED_PAGE_SIZE,
        extend: [PathsV1VehicleBuyInSearchPostParametersQueryExtend.CreatedBy],
        orderBy: [
          {
            field: PathsV1VehicleBuyInSearchPostParametersQueryOrderByField.CreatedAt,
            direction: PathsV1VehicleBuyInSearchPostParametersQueryOrderByDirection.Desc,
          },
        ],
      },
      body: {
        vehicleValuation: [vehicleValuationUuid],
      },
    }),
    [vehicleValuationUuid],
  );
  const buyInsQuery = useVehicleBuyInSearchQuery({
    options: searchOptions,
    enabled: Boolean(vehicleValuationUuid),
    queryOptions: { refetchInterval: VEHICLE_BUY_IN_REFETCH_INTERVAL_MS },
  });
  const buyIns = buyInsQuery.data?.data ?? [];

  if (buyInsQuery.isSuccess && buyIns.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("fab:VehicleBuyIns")}</CardTitle>
        <CardDescription>{t("fab:VehicleValuationBuyInsDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {buyInsQuery.isPending
          ? Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <Skeleton key={index} className="h-16 w-full rounded-lg" />
            ))
          : null}
        {buyInsQuery.isError ? <p className="text-error text-sm">{t("fab:VehicleBuyInListLoadError")}</p> : null}
        {buyIns.length > 0 ? (
          <div className="text-muted-foreground hidden grid-cols-3 gap-x-4 border border-transparent px-3 text-xs font-semibold tracking-wider uppercase sm:grid">
            <span>{t("common:CreatedAt")}</span>
            <span>{t("fab:Employee")}</span>
            <span>{t("common:Status")}</span>
          </div>
        ) : null}
        {buyIns.map((buyIn) => (
          // The buy-in link stretches over the whole row; the user link sits above it to avoid nested links.
          <div
            key={buyIn.uuid}
            className="bg-card border-border hover:bg-muted/30 hover:border-primary/60 relative grid cursor-pointer grid-cols-1 items-center gap-x-4 gap-y-2 rounded-lg border p-3 transition-colors sm:grid-cols-3"
          >
            <Link
              to={`~/fab/buy-in/${buyIn.uuid}`}
              className="text-sm after:absolute after:inset-0 after:rounded-lg after:content-['']"
            >
              {automaticCellRenderer(buyIn.createdAt, {
                dateFormat: "datetime",
              })}
            </Link>
            <UserItem user={buyIn.createdBy} variant={UserItemVariant.Compact} className="relative z-10 min-w-0" />
            <div>
              <VehicleBuyInStatusBadge status={buyIn.status} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
